import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AuthShell from './AuthShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { acceptInvite, fetchMe } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';

export default function AcceptInvite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { hydrate } = useAuth();

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!token) {
      setError('Invite link is missing its token.');
      return;
    }
    setPending(true);
    try {
      const fd = new FormData(event.currentTarget);
      await acceptInvite(token, {
        password: String(fd.get('password') ?? ''),
        fullName: String(fd.get('fullName') ?? '').trim() || undefined,
      });
      const me = await fetchMe();
      hydrate(me);
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not accept invite');
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthShell
      stamp="Invitation"
      title="Join the account"
      subtitle="Set a password to accept the invite. If you already have a HouseOps account on this email, signing in will attach you instead — leave the password blank if so."
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="accept-form">
        <div className="space-y-2">
          <Label htmlFor="fullName" className="font-mono text-[11px] uppercase tracking-wider">
            Your name
          </Label>
          <Input
            id="fullName"
            name="fullName"
            type="text"
            autoComplete="name"
            className="h-11 text-base"
            data-testid="fullName"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="font-mono text-[11px] uppercase tracking-wider">
            Password
          </Label>
          <Input
            id="password"
            name="password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            required
            className="h-11 text-base"
            data-testid="password"
          />
        </div>
        {error ? (
          <p className="text-sm text-warning font-medium" role="alert">
            {error}
          </p>
        ) : null}
        <Button type="submit" className="w-full h-11 text-base" disabled={pending} data-testid="submit">
          {pending ? 'Accepting…' : 'Accept invite'}
        </Button>
      </form>
    </AuthShell>
  );
}
